import React, { useMemo, useEffect, useState, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import Modal from 'react-modal';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import CloseIcon from '@wix/da-ds/pkg/Icons/24x24/Close';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { ScreenSizeContext } from '@wix/da-hooks/pkg/useMeasureElement/ScreenSizeContext';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';
import { parse as parseSrcSet } from '../../utils/srcset';
import s from './ZoomModal.scss';
function isBiggerThanViewport(width, height) {
    if (typeof window === 'undefined' || !width || !height) {
        return false;
    }
    return width > window.innerWidth || height > window.innerHeight;
}
var ZoomStage = /*#__PURE__*/ function(ZoomStage) {
    ZoomStage[ZoomStage["INITIAL"] = 0] = "INITIAL";
    ZoomStage[ZoomStage["EXPANDED"] = 1] = "EXPANDED";
    return ZoomStage;
}(ZoomStage || {});
export const ZoomModal = ({ src, srcSet, width, height, isPixel, onClose })=>{
    const { t } = useTranslation();
    const [zoomStage, setZoomStage] = useState(0);
    const isMobile = useContext(MobileContext);
    const { width: screenWidth } = useContext(ScreenSizeContext);
    const largestImage = useMemo(()=>parseSrcSet(srcSet || '').concat({
            width,
            height,
            url: src
        }).filter((source)=>source.width).sort(({ width: a = 0 }, { width: b = 0 })=>a - b).pop(), [
        srcSet,
        width,
        height,
        src
    ]);
    const maxWidth = Math.max((largestImage === null || largestImage === void 0 ? void 0 : largestImage.width) || 0, screenWidth);
    const canZoom = isBiggerThanViewport(width, height);
    // close when escape is used
    useEffect(()=>{
        const onKeyDown = (e)=>{
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return ()=>window.removeEventListener('keydown', onKeyDown);
    }, [
        onClose
    ]);
    const toggleZoomStage = ()=>{
        if (canZoom) {
            // For mobile, we only want the initial zoom mode, then users can pinch+zoom all they want.
            if (zoomStage === 0 && !isMobile) {
                setZoomStage(1);
            } else {
                setZoomStage(0);
            }
        }
    };
    const doClose = useCallback(()=>{
        yieldToMain(()=>{
            onClose();
        });
    }, [
        onClose
    ]);
    const isFullyZoomed = canZoom && zoomStage === 1;
    // wrapping in a modal to avoid the can of worms with scrolling the underlying page and all
    // NoLeakyScroll only goes that far, and this seems more performant overall
    return /*#__PURE__*/ React.createElement(Modal, {
        className: classnames(s['modal'], zoomStage === 1 && s['modal-expanded']),
        isOpen: true,
        overlayClassName: s['overlay'],
        onRequestClose: doClose
    }, /*#__PURE__*/ React.createElement("button", {
        className: s['unzoom-button'],
        "aria-label": t('deviationview.zoomModal.closeButton.altLabel'),
        onClick: doClose
    }, /*#__PURE__*/ React.createElement(CloseIcon, {
        size: IconSize.BIG,
        className: s['unzoom-button-icon']
    })), /*#__PURE__*/ React.createElement("div", {
        className: s['zoom-viewport']
    }, /*#__PURE__*/ React.createElement("img", {
        alt: "",
        className: classnames(s['image'], isPixel && s['pixel'], canZoom && zoomStage === 0 && s['image-initial-zoom-level'], canZoom && zoomStage === 1 && s['image-expanded-zoom-level']),
        src: isFullyZoomed && (largestImage === null || largestImage === void 0 ? void 0 : largestImage.url) ? largestImage.url : src,
        style: isFullyZoomed ? {
            maxWidth
        } : undefined,
        onClick: toggleZoomStage
    })));
};
ZoomModal.displayName = 'ZoomModal';
export default ZoomModal;

//# sourceMappingURL=ZoomModal.js.map